import Adjust from "@adjustcom/adjust-web-sdk";

import appConfig from "@/config/app-config";
import { captureException } from "@/monitoring";

// Adjust attribution: SDK lifecycle + per-account event gating. Lifecycle is
// driven by `AdjustProvider` (consent reconciliation); the semantic track
// helpers are safe to call anywhere — they no-op until the SDK is ready.

// Dashboard event tokens (not secrets — they map a trackEvent call to a
// dashboard event). `unique` mirrors the dashboard config: true = once per device.
const EVENT_TOKENS = {
  signup: "2s8eag", // unique: true
  onboarding_complete: "8x9xj3", // unique: true
  first_follow: "ruep7i", // unique: true
  first_engagement: "kp1j9u", // unique: true
  first_post: "85lz2m", // unique: true
  d7_active_session: "oqa6c7", // unique: false
  d7_active_engagement: "v0osk5", // unique: true
} as const;

type AdjustEventName = keyof typeof EVENT_TOKENS;

let initialized = false;
let consentGranted = false;

const isReady = () => initialized && consentGranted;

function initAdjust(): void {
  if (initialized || typeof window === "undefined") return;
  const appToken = appConfig.ADJUST_APP_TOKEN;
  if (!appToken) return;
  // Sandbox for dev/test so local traffic stays out of production reporting and
  // is visible in the Adjust Testing Console; production only for prod builds.
  const isProd = appConfig.NODE_ENV === "production";
  try {
    Adjust.initSdk({
      appToken,
      environment: isProd ? "production" : "sandbox",
      logLevel: "error",
    });
    initialized = true;
  } catch (error) {
    captureException(error, { tags: { component: "Adjust", op: "init" } });
  }
}

// Reconcile the SDK with consent: granted -> init (idempotent), revoked -> forget.
export function syncAdjust({ consent }: { consent: boolean }): void {
  consentGranted = consent;
  if (consent) {
    initAdjust();
  } else if (initialized) {
    try {
      Adjust.gdprForgetMe();
    } catch (error) {
      captureException(error, { tags: { component: "Adjust", op: "forget" } });
    }
  }
}

export function setAdjustAccountId(accountId: string): void {
  if (!isReady()) return;
  try {
    Adjust.addGlobalCallbackParameters([
      { key: "account_id", value: accountId },
    ]);
  } catch (error) {
    captureException(error, { tags: { component: "Adjust", op: "identify" } });
  }
}

function trackEvent(name: AdjustEventName): void {
  if (!isReady()) return;
  try {
    Adjust.trackEvent({ eventToken: EVENT_TOKENS[name] });
  } catch (error) {
    captureException(error, {
      tags: { component: "Adjust", op: "trackEvent" },
      extra: { event: name },
    });
  }
}

// Per-account persistence under one localStorage key, scoped so two accounts on
// one device don't share state. Per-device: a first_* can re-fire for the same
// account on a new browser (accepted trade-off). `install_at` = signup time.
const ROOT_KEY = "adjust";

interface AccountState {
  install_at?: number;
  [event: string]: boolean | number | undefined;
}

let accountScope: string | null = null;

export function setAdjustAccountScope(accountId: string | null): void {
  accountScope = accountId;
}

function readAccounts(): Record<string, AccountState> {
  try {
    if (typeof window === "undefined") return {};
    const raw = window.localStorage.getItem(ROOT_KEY);
    return raw ? (JSON.parse(raw)?.accounts ?? {}) : {};
  } catch (error) {
    captureException(error, {
      tags: { component: "Adjust", op: "storage.read" },
    });
    return {};
  }
}

function current(): AccountState | undefined {
  return readAccounts()[accountScope ?? "anon"];
}

function update(patch: AccountState): void {
  try {
    if (typeof window === "undefined") return;
    const accounts = readAccounts();
    const id = accountScope ?? "anon";
    accounts[id] = { ...accounts[id], ...patch };
    window.localStorage.setItem(ROOT_KEY, JSON.stringify({ accounts }));
  } catch (error) {
    captureException(error, {
      tags: { component: "Adjust", op: "storage.write" },
    });
  }
}

// Gating is localStorage-only (account counts are unreliable — onboarding itself
// follows accounts/groups). Chain: signup -> onboarding_complete -> engagement /
// first_*. d7_* fire when the open/action lands in the [7d, 8d) window after this
// account's signup (install day = 0).
const DAY_MS = 24 * 60 * 60 * 1000;

export type EngagementType = "like" | "comment" | "retruth" | "post" | "follow";

function trackOnce(name: AdjustEventName): void {
  if (!isReady() || current()?.[name] === true) return;
  trackEvent(name);
  update({ [name]: true });
}

function isDay7(): boolean {
  const installAt = current()?.install_at;
  if (typeof installAt !== "number") return false;
  return Math.floor((Date.now() - installAt) / DAY_MS) === 7;
}

// Fires the recurring d7_active_session when the session lands in the day-7
// window. May re-run on consent/account/route changes; the event is non-unique
// so re-fires within the window are expected.
export function bootstrapSession(): void {
  if (!isReady() || current()?.signup !== true) return;
  if (isDay7()) trackEvent("d7_active_session");
}

// SMS verified + account submitted. Scope to the new account first (auth context
// hasn't updated yet), stamp its install anchor, then record signup.
export function trackSignup(accountId?: string): void {
  if (accountId) setAdjustAccountScope(accountId);
  if (!isReady()) return;
  if (typeof current()?.install_at !== "number") {
    update({ install_at: Date.now() });
  }
  trackOnce("signup");
}

export function trackOnboardingComplete(): void {
  if (current()?.signup !== true) return;
  trackOnce("onboarding_complete");
}

// Only fires after onboarding, so follows made during onboarding don't count.
export function trackEngagement(type: EngagementType): void {
  if (!isReady() || current()?.onboarding_complete !== true) return;
  trackOnce("first_engagement");
  if (type === "follow") trackOnce("first_follow");
  if (type === "post") trackOnce("first_post");
  if (isDay7()) trackOnce("d7_active_engagement");
}
